import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { apiErrorMessage, listCourses, listMappableCourses } from "../../api/assessmentHierarchy";
import {
  ACCESS_TYPES,
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getContentTree,
  saveContentMapping,
  listCourseTests,
  saveTestMapping,
} from "../../api/assessmentCategories";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import useDebouncedValue from "./useDebouncedValue";
import useColumnVisibility from "./useColumnVisibility";
import ColumnsMenuButton from "./ColumnsMenuButton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const primaryButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 2,
  background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
  "&:hover": { background: "linear-gradient(135deg, #123a80 0%, #0d47a1 100%)" },
  "&.Mui-disabled": { background: "#b0bec5" },
};

const cancelButtonSx = {
  flex: "0 0 auto",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 2,
  color: "#555",
  backgroundColor: "#eceff1",
  "&:hover": { backgroundColor: "#e0e3e6" },
};

function DialogHeader({ icon, title, subtitle, onClose }) {
  return (
    <Box
      sx={{
        position: "relative",
        p: 3,
        background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
        color: "#fff",
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.18)",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>{title}</Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>{subtitle}</Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        color: cfg.color,
        borderColor: cfg.color,
        backgroundColor: cfg.bg,
      }}
    />
  );
}

function AccessChip({ value }) {
  const isPremium = value === "PREMIUM";
  return (
    <Chip
      label={isPremium ? "Premium" : "Free"}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        color: isPremium ? "#b26a00" : "#2e7d32",
        borderColor: isPremium ? "#b26a00" : "#2e7d32",
        backgroundColor: isPremium ? "#fff3e0" : "#e8f5e9",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Add / edit category
// ---------------------------------------------------------------------------

function CategoryFormDialog({ open, category, exams, defaultExamId, onClose, onSave }) {
  const [examId, setExamId] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [accessType, setAccessType] = useState("FREE");
  const [price, setPrice] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [previewChapters, setPreviewChapters] = useState("");
  const [previewTopics, setPreviewTopics] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [readinessTarget, setReadinessTarget] = useState("");
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setExamId(category?.courseId ?? defaultExamId ?? "");
    setName(category?.name || "");
    setAccessType(category?.accessType || "FREE");
    setPrice(category?.price ?? "");
    setDurationDays(category?.durationDays ?? "");
    setPreviewChapters(category?.freePreviewChapterCount ?? "");
    setPreviewTopics(category?.freePreviewTopicCount ?? "");
    setDisplayOrder(category?.displayOrder ?? "");
    setStatus(category?.status || "ACTIVE");
    setReadinessTarget(category?.readinessTargetPercentage ?? "");
    setSelectedCourseIds(category?.courseIds || []);
    listMappableCourses()
      .then(setCourseOptions)
      .catch(() => toast.error("Failed to load academic courses"));
  }, [open, category, defaultExamId]);

  const isPremium = accessType === "PREMIUM";
  const priceInvalid = isPremium && !(Number(price) > 0);
  const durationInvalid =
    isPremium && !(Number(durationDays) > 0 && Number.isInteger(Number(durationDays)));
  const readinessInvalid =
    readinessTarget !== "" && (Number(readinessTarget) < 0 || Number(readinessTarget) > 100);
  const isValid =
    (Boolean(category) || examId !== "") &&
    name.trim().length > 0 && !priceInvalid && !durationInvalid && !readinessInvalid;

  const toNumber = (v) => (v === "" || v == null ? undefined : Number(v));

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: category?.id,
        payload: {
          ...(category ? {} : { courseId: examId }),
          name: name.trim(),
          accessType,
          price: isPremium ? Number(price) : undefined,
          durationDays: isPremium ? Number(durationDays) : undefined,
          freePreviewChapterCount: isPremium ? toNumber(previewChapters) : undefined,
          freePreviewTopicCount: isPremium ? toNumber(previewTopics) : undefined,
          displayOrder: toNumber(displayOrder),
          readinessTargetPercentage: toNumber(readinessTarget),
          courseIds: selectedCourseIds,
          active: status === "ACTIVE",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <DialogHeader
        icon={<CategoryOutlinedIcon />}
        title={category ? "Edit Course" : "Add New Course"}
        subtitle={category ? "Update course details" : "Set up name, access and pricing"}
        onClose={onClose}
      />
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <FormControl fullWidth size="small" required disabled={Boolean(category)}>
              <InputLabel>Exam</InputLabel>
              <Select label="Exam *" value={examId} onChange={(e) => setExamId(e.target.value)}>
                {exams.map((x) => (
                  <MenuItem key={x.id} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Course Name"
              fullWidth
              size="small"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !name.trim()}
              helperText={
                nameTouched && !name.trim()
                  ? "Please fill out this field"
                  : "A sellable package built from a subset of the exam's content"
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Access Type</InputLabel>
              <Select
                label="Access Type *"
                value={accessType}
                onChange={(e) => setAccessType(e.target.value)}
              >
                {ACCESS_TYPES.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a === "PREMIUM" ? "Premium" : "Free"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isPremium && (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    label="Price"
                    type="number"
                    size="small"
                    required
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    error={priceInvalid && price !== ""}
                    helperText={priceInvalid && price !== "" ? "Must be greater than 0" : " "}
                    inputProps={{ min: 0, step: "0.01" }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Duration (days)"
                    type="number"
                    size="small"
                    required
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    error={durationInvalid && durationDays !== ""}
                    helperText={
                      durationInvalid && durationDays !== "" ? "Must be a whole number > 0" : " "
                    }
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    label="Free Preview Chapters"
                    type="number"
                    size="small"
                    value={previewChapters}
                    onChange={(e) => setPreviewChapters(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Free Preview Topics"
                    type="number"
                    size="small"
                    value={previewTopics}
                    onChange={(e) => setPreviewTopics(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </>
            )}
            <TextField
              label="Display Order (optional)"
              type="number"
              fullWidth
              size="small"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={courseOptions}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
                  {option.name}
                </li>
              )}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={courseOptions.filter((o) => selectedCourseIds.includes(o.id))}
              onChange={(_, value) => setSelectedCourseIds(value.map((o) => o.id))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Available to Academic Courses"
                  helperText="Students only see this course if their academic course is selected here"
                />
              )}
            />
            <TextField
              label="Readiness Target (%)"
              type="number"
              fullWidth
              size="small"
              value={readinessTarget}
              onChange={(e) => setReadinessTarget(e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              error={readinessInvalid}
              helperText={
                readinessInvalid
                  ? "Must be between 0 and 100"
                  : category?.readinessTargetPercentage != null
                    ? "Leave blank to keep the current value"
                    : "Default is 85% when left blank"
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControlLabel
              label={status === "ACTIVE" ? "Active" : "Inactive"}
              control={
                <Switch
                  checked={status === "ACTIVE"}
                  onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2e7d32",
                    },
                  }}
                />
              }
            />
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button onClick={onClose} disabled={submitting} sx={cancelButtonSx}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{ flex: 1, ...primaryButtonSx }}
          >
            {category ? "Save Changes" : "Create Course"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Content mapping (Subject > Chapter > Topic > Sub-Topic / Video checkbox tree)
// ---------------------------------------------------------------------------

const LEVEL_LABEL = {
  subject: "Subject",
  chapter: "Chapter",
  topic: "Topic",
  subTopic: "Sub-Topic",
  video: "Video",
};

const emptySelection = () => ({
  subject: new Set(),
  chapter: new Set(),
  topic: new Set(),
  subTopic: new Set(),
  video: new Set(),
});

// Flatten the API tree into a uniform { level, id, label, children } shape.
function buildTree(apiTree) {
  const subTopicNode = (st) => ({ level: "subTopic", id: st.id, label: st.name, children: [] });
  const videoNode = (v) => ({ level: "video", id: v.id, label: v.title, children: [] });
  const topicNode = (t) => ({
    level: "topic",
    id: t.id,
    label: t.name,
    children: [...(t.subTopics || []).map(subTopicNode), ...(t.videos || []).map(videoNode)],
  });
  const chapterNode = (c) => ({
    level: "chapter",
    id: c.id,
    label: c.name,
    children: (c.topics || []).map(topicNode),
  });
  return (apiTree?.subjects || []).map((s) => ({
    level: "subject",
    id: s.id,
    label: s.name,
    children: (s.chapters || []).map(chapterNode),
  }));
}

// Initial selection comes from the `included` flags in the API tree.
function collectIncluded(apiTree) {
  const sel = emptySelection();
  (apiTree?.subjects || []).forEach((s) => {
    if (s.included) sel.subject.add(s.id);
    (s.chapters || []).forEach((c) => {
      if (c.included) sel.chapter.add(c.id);
      (c.topics || []).forEach((t) => {
        if (t.included) sel.topic.add(t.id);
        (t.subTopics || []).forEach((st) => st.included && sel.subTopic.add(st.id));
        (t.videos || []).forEach((v) => v.included && sel.video.add(v.id));
      });
    });
  });
  return sel;
}

// The node plus every descendant, as [level, id] pairs.
function walk(node, out = []) {
  out.push([node.level, node.id]);
  node.children.forEach((child) => walk(child, out));
  return out;
}

// Checked = included AND everything below it is included.
const isFullyIncluded = (node, sel) =>
  sel[node.level].has(node.id) && node.children.every((c) => isFullyIncluded(c, sel));

const hasAnyIncluded = (node, sel) =>
  sel[node.level].has(node.id) || node.children.some((c) => hasAnyIncluded(c, sel));

function TreeRow({ node, depth, pathKey, ancestors, sel, expanded, toggleExpanded, onToggle }) {
  const key = `${pathKey}/${node.level}:${node.id}`;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(key);
  const checked = isFullyIncluded(node, sel);
  const indeterminate = !checked && hasAnyIncluded(node, sel);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          pl: depth * 3,
          py: 0.25,
          "&:hover": { backgroundColor: "rgba(23,71,158,0.04)" },
          borderRadius: 1,
        }}
      >
        <Box sx={{ width: 28, display: "flex", justifyContent: "center" }}>
          {hasChildren && (
            <IconButton size="small" onClick={() => toggleExpanded(key)}>
              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          onChange={() => onToggle(node, ancestors, !checked)}
        />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: depth < 2 ? 600 : 400 }}>
          {node.label}
        </Typography>
        <Chip
          label={LEVEL_LABEL[node.level]}
          size="small"
          variant="outlined"
          sx={{ ml: 1, height: 18, fontSize: "0.62rem", color: "text.secondary" }}
        />
      </Stack>
      {hasChildren && (
        <Collapse in={isOpen} unmountOnExit>
          {node.children.map((child) => (
            <TreeRow
              key={`${child.level}:${child.id}`}
              node={child}
              depth={depth + 1}
              pathKey={key}
              ancestors={[...ancestors, node]}
              sel={sel}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
              onToggle={onToggle}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

function ContentMappingDialog({ open, category, course, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tree, setTree] = useState([]);
  const [sel, setSel] = useState(emptySelection);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!open || !category || !course) return;
    let cancelled = false;
    setLoading(true);
    getContentTree(course.id, category.id)
      .then((apiTree) => {
        if (cancelled) return;
        setTree(buildTree(apiTree));
        setSel(collectIncluded(apiTree));
        setExpanded(new Set());
      })
      .catch((err) => {
        if (cancelled) return;
        setTree([]);
        toast.error(apiErrorMessage(err, "Failed to load exam content"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, category, course]);

  const toggleExpanded = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Checking a node selects it and everything below it, and pulls in the
  // ancestors on the clicked path (the backend rejects a child whose parent
  // isn't part of the same mapping). Unchecking clears the node and its subtree.
  const handleToggle = (node, ancestors, checked) => {
    setSel((prev) => {
      const next = {
        subject: new Set(prev.subject),
        chapter: new Set(prev.chapter),
        topic: new Set(prev.topic),
        subTopic: new Set(prev.subTopic),
        video: new Set(prev.video),
      };
      if (checked) {
        walk(node).forEach(([level, id]) => next[level].add(id));
        ancestors.forEach((a) => next[a.level].add(a.id));
      } else {
        walk(node).forEach(([level, id]) => next[level].delete(id));
      }
      return next;
    });
  };

  const setAll = (checked) => {
    setSel(() => {
      const next = emptySelection();
      if (checked) tree.forEach((root) => walk(root).forEach(([level, id]) => next[level].add(id)));
      return next;
    });
  };

  const expandAll = () => {
    const keys = new Set();
    const visit = (node, pathKey) => {
      const key = `${pathKey}/${node.level}:${node.id}`;
      if (node.children.length) keys.add(key);
      node.children.forEach((c) => visit(c, key));
    };
    tree.forEach((root) => visit(root, ""));
    setExpanded(keys);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContentMapping(category.id, {
        subjectIds: Array.from(sel.subject),
        chapterIds: Array.from(sel.chapter),
        topicIds: Array.from(sel.topic),
        subTopicIds: Array.from(sel.subTopic),
        videoIds: Array.from(sel.video),
      });
      toast.success("Course content updated!");
      onClose(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save course content"));
    } finally {
      setSaving(false);
    }
  };

  const totalSelected =
    sel.subject.size + sel.chapter.size + sel.topic.size + sel.subTopic.size + sel.video.size;

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <DialogHeader
        icon={<AccountTreeOutlinedIcon />}
        title={`Manage Content — ${category?.name ?? ""}`}
        subtitle={`Pick what this course includes from ${course?.name ?? "the exam"}`}
        onClose={() => onClose(false)}
      />
      <DialogContent sx={{ pt: 2, backgroundColor: "#f7f8fb" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
          <Button size="small" onClick={() => setAll(true)} sx={{ textTransform: "none" }}>
            Select all
          </Button>
          <Button size="small" onClick={() => setAll(false)} sx={{ textTransform: "none" }}>
            Clear all
          </Button>
          <Button size="small" onClick={expandAll} sx={{ textTransform: "none" }}>
            Expand all
          </Button>
          <Button size="small" onClick={() => setExpanded(new Set())} sx={{ textTransform: "none" }}>
            Collapse all
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {sel.subject.size} subjects · {sel.chapter.size} chapters · {sel.topic.size} topics ·{" "}
            {sel.subTopic.size} sub-topics · {sel.video.size} videos
          </Typography>
        </Stack>
        <Card sx={{ boxShadow: 1, borderRadius: 3, minHeight: 240, maxHeight: "55vh", overflow: "auto" }}>
          <CardContent sx={{ p: 1.5 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : tree.length === 0 ? (
              <Typography sx={{ textAlign: "center", py: 4 }} color="text.secondary">
                This exam has no active subjects yet.
              </Typography>
            ) : (
              tree.map((root) => (
                <TreeRow
                  key={`${root.level}:${root.id}`}
                  node={root}
                  depth={0}
                  pathKey=""
                  ancestors={[]}
                  sel={sel}
                  expanded={expanded}
                  toggleExpanded={toggleExpanded}
                  onToggle={handleToggle}
                />
              ))
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button onClick={() => onClose(false)} disabled={saving} sx={cancelButtonSx}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={saving}
            onClick={handleSave}
            disabled={loading}
            sx={{ flex: 1, ...primaryButtonSx }}
          >
            Save Content{totalSelected ? ` (${totalSelected} items)` : ""}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Test mapping
// ---------------------------------------------------------------------------

function TestMappingDialog({ open, category, course, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !category || !course) return;
    let cancelled = false;
    setLoading(true);
    setSearch("");
    listCourseTests(course.id, category.id)
      .then((rows) => {
        if (cancelled) return;
        setTests(rows);
        setSelected(new Set(rows.filter((t) => t.included).map((t) => t.id)));
      })
      .catch((err) => {
        if (cancelled) return;
        setTests([]);
        toast.error(apiErrorMessage(err, "Failed to load tests"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, category, course]);

  const visibleTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? tests.filter((t) => t.name?.toLowerCase().includes(q)) : tests;
  }, [tests, search]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    visibleTests.length > 0 && visibleTests.every((t) => selected.has(t.id));

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleTests.forEach((t) => (allVisibleSelected ? next.delete(t.id) : next.add(t.id)));
      return next;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTestMapping(category.id, Array.from(selected));
      toast.success("Course tests updated!");
      onClose(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save course tests"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <DialogHeader
        icon={<QuizOutlinedIcon />}
        title={`Manage Tests — ${category?.name ?? ""}`}
        subtitle={`Tests belonging to ${course?.name ?? "the exam"}`}
        onClose={() => onClose(false)}
      />
      <DialogContent sx={{ pt: 2, backgroundColor: "#f7f8fb" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search tests"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5, backgroundColor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <Card sx={{ boxShadow: 1, borderRadius: 3, minHeight: 200, maxHeight: "50vh", overflow: "auto" }}>
          <CardContent sx={{ p: 1.5 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : visibleTests.length === 0 ? (
              <Typography sx={{ textAlign: "center", py: 4 }} color="text.secondary">
                {tests.length === 0 ? "This exam has no tests yet." : "No tests match your search."}
              </Typography>
            ) : (
              <>
                <FormControlLabel
                  sx={{ ml: 0, mb: 0.5 }}
                  label={
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Select all{search ? " shown" : ""}
                    </Typography>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && visibleTests.some((t) => selected.has(t.id))}
                      onChange={toggleAllVisible}
                    />
                  }
                />
                <Divider sx={{ mb: 0.5 }} />
                {visibleTests.map((t) => (
                  <FormControlLabel
                    key={t.id}
                    sx={{ display: "flex", ml: 0 }}
                    label={<Typography sx={{ fontSize: "0.85rem" }}>{t.name}</Typography>}
                    control={
                      <Checkbox size="small" checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
                    }
                  />
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button onClick={() => onClose(false)} disabled={saving} sx={cancelButtonSx}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={saving}
            onClick={handleSave}
            disabled={loading}
            sx={{ flex: 1, ...primaryButtonSx }}
          >
            Save Tests ({selected.size} selected)
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Courses (categories) across all exams
// ---------------------------------------------------------------------------

const columns = [
  { key: "id", label: "ID", sx: { width: 60, minWidth: 60 } },
  { key: "name", label: "Name", sx: { minWidth: 200 } },
  { key: "exam", label: "Exam", sx: { minWidth: 160 } },
  { key: "access", label: "Access", sx: { minWidth: 100 }, align: "center" },
  { key: "courses", label: "Academic Courses", sx: { width: 400, minWidth: 400 } },
  { key: "readinessTarget", label: "Readiness Target", sx: { minWidth: 130 }, align: "center" },
  { key: "price", label: "Price", sx: { minWidth: 90 }, align: "center" },
  { key: "duration", label: "Duration (days)", sx: { minWidth: 120 }, align: "center" },
  { key: "preview", label: "Free Preview (Ch / Tp)", sx: { minWidth: 150 }, align: "center" },
  { key: "subjects", label: "Subjects", sx: { minWidth: 90 }, align: "center" },
  { key: "chapters", label: "Chapters", sx: { minWidth: 90 }, align: "center" },
  { key: "topics", label: "Topics", sx: { minWidth: 80 }, align: "center" },
  { key: "subTopics", label: "Sub-Topics", sx: { minWidth: 100 }, align: "center" },
  { key: "videos", label: "Videos", sx: { minWidth: 80 }, align: "center" },
  { key: "displayOrder", label: "Display Order", sx: { minWidth: 110 }, align: "center" },
  { key: "status", label: "Status", sx: { minWidth: 100 }, align: "center" },
];

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function AssessmentCourseCategories() {
  const [categories, setCategories] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const requestSeq = useRef(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const { visibleColumns, toggleColumn, visibleColumnDefs } = useColumnVisibility(
    "assessmentCourseCategoriesHiddenColumns",
    columns,
  );
  const [exams, setExams] = useState([]);
  const [examFilter, setExamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contentTarget, setContentTarget] = useState(null);
  const [testsTarget, setTestsTarget] = useState(null);

  const examsById = useMemo(() => new Map(exams.map((x) => [x.id, x])), [exams]);

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setExams)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  const loadCategories = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    listCategories({ courseId: examFilter, isActive: statusFilter ? statusFilter === "ACTIVE" : undefined, search: debouncedSearch, page, size: rowsPerPage })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        // Deleting the last row of the last page leaves it empty: step back one page.
        if (data.content.length === 0 && page > 0 && data.totalPages > 0) {
          setPage(data.totalPages - 1);
          return;
        }
        setCategories(data.content);
        setTotalElements(data.totalElements ?? data.content.length);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setCategories([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load courses"));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [examFilter, statusFilter, debouncedSearch, page, rowsPerPage]);

  // A new search or exam filter starts again from the first page.
  useEffect(() => {
    setPage(0);
  }, [examFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSave = async ({ id, payload }) => {
    try {
      if (id) await updateCategory(id, payload);
      else await createCategory(payload);
      toast.success(id ? "Course updated successfully!" : "Course created successfully!");
      setFormOpen(false);
      loadCategories();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save course"));
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    try {
      await deleteCategory(target.id);
      toast.success("Course deleted!");
      setDeleteTarget(null);
      loadCategories();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to delete course"));
    }
  };

  const renderCell = (key, row) => {
    switch (key) {
      case "id":
        return (
          <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
        );
      case "name":
        return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.name}</Typography>;
      case "exam":
        return examsById.get(row.courseId)?.name ?? "—";
      case "access":
        return <AccessChip value={row.accessType} />;
      case "courses":
        return row.courseNames?.length ? row.courseNames.join(", ") : "—";
      case "readinessTarget":
        return row.readinessTargetPercentage != null ? `${row.readinessTargetPercentage}%` : "—";
      case "price":
        return row.accessType === "PREMIUM" && row.price != null ? `₹${row.price}` : "—";
      case "duration":
        return dash(row.durationDays);
      case "preview":
        return row.accessType === "PREMIUM"
          ? `${row.freePreviewChapterCount ?? 0} / ${row.freePreviewTopicCount ?? 0}`
          : "—";
      case "subjects":
        return row.subjectCount;
      case "chapters":
        return row.chapterCount;
      case "topics":
        return row.topicCount;
      case "subTopics":
        return row.subTopicCount;
      case "videos":
        return row.videoCount;
      case "displayOrder":
        return dash(row.displayOrder);
      case "status":
        return <StatusChip value={row.status} />;
      default:
        return null;
    }
  };

  const actionButton = (title, color, Icon, onClick) => (
    <Tooltip title={title} arrow placement="top">
      <IconButton
        size="small"
        color={color}
        onClick={onClick}
        sx={{ "&:hover": { backgroundColor: `${color}.light`, color: "white" } }}
      >
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Box>
        <Box>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2, flexWrap: "wrap" }}
            useFlexGap
          >
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search courses"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260, backgroundColor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <FormControl size="small" sx={{ minWidth: 200, backgroundColor: "#fff" }}>
                <InputLabel>Exam</InputLabel>
                <Select label="Exam" value={examFilter} onChange={(e) => setExamFilter(e.target.value)}>
                  <MenuItem value="">All Exams</MenuItem>
                  {exams.map((x) => (
                    <MenuItem key={x.id} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <ColumnsMenuButton allColumns={columns} visibleColumns={visibleColumns} onToggle={toggleColumn} />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                sx={{ ...primaryButtonSx, px: 2.5 }}
              >
                Add Course
              </Button>
            </Stack>
          </Stack>
          <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: "70vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        sx={{
                          ...c.sx,
                          padding: "10px 12px",
                          backgroundColor: ACCENT,
                          color: "white",
                          fontWeight: 600,
                        }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 210,
                        padding: "10px 12px",
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
                  ) : categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                        <AssessmentEmptyState message="No courses found" hint="Try changing your search or filters." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            align={c.align}
                            sx={{ padding: "10px 12px", ...c.sx }}
                          >
                            {renderCell(c.key, row)}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            padding: "10px 12px",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {actionButton("Edit Course", "primary", EditIcon, () => {
                              setEditing(row);
                              setFormOpen(true);
                            })}
                            {actionButton("Manage Content", "primary", AccountTreeOutlinedIcon, () =>
                              setContentTarget(row),
                            )}
                            {actionButton("Manage Tests", "primary", QuizOutlinedIcon, () =>
                              setTestsTarget(row),
                            )}
                            {actionButton("Delete Course", "error", DeleteIcon, () =>
                              setDeleteTarget(row),
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
            }}
          />
          </Card>
        </Box>
      </Box>

      <CategoryFormDialog
        open={formOpen}
        category={editing}
        exams={exams}
        defaultExamId={examFilter}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <ContentMappingDialog
        open={Boolean(contentTarget)}
        category={contentTarget}
        course={examsById.get(contentTarget?.courseId)}
        onClose={(saved) => {
          setContentTarget(null);
          if (saved) loadCategories();
        }}
      />

      <TestMappingDialog
        open={Boolean(testsTarget)}
        category={testsTarget}
        course={examsById.get(testsTarget?.courseId)}
        onClose={() => setTestsTarget(null)}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Deleting "${deleteTarget?.name ?? "this course"}" removes it from the course list and hides it from students (soft delete). It can't be restored from this screen.`}
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}

export default AssessmentCourseCategories;
